/**
 * General utils for array & object manipulation
 */

let Utils;

/**
 * Create an object with all properties set to the defaultValue
 * @param  {string[]} properties       [description]
 * @param  {*} [defaultValue=0] [description]
 * @return {Object}                  [description]
 */
export const createInitialObject = (properties, defaultValue = 0) =>
    properties.reduce(
        (object, key) => ({
            ...object,
            [key]: defaultValue,
        }),
        {},
    );

/**
 * Sum properties of all objects in an array
 * @param  {Object[]} data - an array of objects
 * @param  {string[]} keys - list of properties that need to be summed
 * @return {Object} a single object with all properties summed
 */
export const sumArrayProperties = (data, keys, initValue = 0) => {
    const initialObject = Utils.createInitialObject(keys, initValue);
    if (!data) return initialObject;
    return data.reduce(
        (totalsAccu, current) =>
            keys.reduce(
                (object, key) => {
                    const accuValue = object[key] === null ? null : object[key] || 0;
                    const currentValue = current[key] === null ? null : current[key] || 0;
                    let result;
                    if (accuValue === null) {
                        result = currentValue;
                    } else if (currentValue === null) {
                        result = accuValue;
                    } else {
                        result = accuValue + currentValue;
                    }

                    return {
                        ...object,
                        [key]: result,
                    };
                },
                { ...totalsAccu },
            ),
        initialObject,
    );
};

/**
 * Group an array of objects by a given property & return an object where each
 * key is the value of the property, & each value the row it was in
 * @param  {Object[]}  array    - the array of objects to group
 * @param  {string} property - the property to group by
 * @return {Object} an object with each key/value pair being the property value/row associated
 */
export const groupBy = (array, property) => {
    if (!array) return {};
    return array.reduce((groups, row) => {
        if (!(property in row)) return groups;

        const propertyValue = row[property];
        const groupMatch = groups[propertyValue];
        const groupRows = groupMatch ? groupMatch.slice(0) : [];

        return {
            ...groups,
            [propertyValue]: groupRows.concat([{ ...row }]),
        };
    }, {});
};

/**
 * Merge 2 arrays of objects into one. Only rows from the baseArray are returned. Extra rows in arrayToMerge are ignored
 * @param  {object[]} baseArray    - array to merge into
 * @param  {object[]} arrayToMerge - array to merge
 * @param  {object}   mergeOptions   - options, such as match checking & default values
 * @param  {string}   mergeOptions.mergeKey      - key to store the arrayToMerge row under
 * @param  {callback} mergeOptions.objectMatcher - function that indicates if 2 rows match
 *                    takes 2 parameters: a row from the baseArray, and a row from the arrayToMerge
 * @param  {any}      mergeOptions.noMatchValue  - value to assign if arrayToMerge does not contain a matching row
 * @return {object[]} a new array, with each object containing the baseArray, and the value from the arrayToMerge under the mergeKey
 */
export const mergeArraysOfObjects = (baseArray, arrayToMerge, mergeOptions) => {
    const { mergeKey, objectMatcher, noMatchValue } = mergeOptions;
    if (!baseArray) return [];

    return baseArray.map((baseRow) => {
        const match = arrayToMerge && arrayToMerge.find((row) => objectMatcher(baseRow, row));
        return {
            ...baseRow,
            [mergeKey]: match ? { ...match } : noMatchValue,
        };
    });
};

/**
 * Applies a transformer to every value in an object
 * @param  {object}   object           - object to transform
 * @param  {callback} valueTransformer - function to transform the value
 * @param  {array}    extraArgs        - array of extra parameters to call the transformer with
 * @return {object} new object with transformed properties
 */
export const transformObjectValues = (object, valueTransformer, extraArgs = []) =>
    Object.entries(object).reduce((accu, entry) => {
        const args = [entry[1], ...extraArgs];
        return {
            ...accu,
            [entry[0]]: valueTransformer(...args),
        };
    }, {});

export default Utils = {
    createInitialObject,
    sumArrayProperties,
    groupBy,
    mergeArraysOfObjects,
    transformObjectValues,
};
